import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

export default function useQRConnection(authAxios, { onConnected, onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [loading, setLoading] = useState(true);
  const [connectedOk, setConnectedOk] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [resetting, setResetting] = useState(false);
  const [stuckReconnecting, setStuckReconnecting] = useState(false);
  // Lease-block diagnostics — set when connect returns owner_lease_held_by_another_instance
  const [leaseInfo, setLeaseInfo] = useState(null);
  const leaseInfoRef = useRef(null);

  const pollRef = useRef(null);
  const statusRef = useRef("connecting");
  const pollFailCount = useRef(0);
  const reconnectingSince = useRef(null);

  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const _setLeaseInfo = (info) => {
    leaseInfoRef.current = info;
    setLeaseInfo(info);
  };

  const startConnection = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    pollFailCount.current = 0;
    try {
      const res = await authAxiosRef.current.post("/chatbot/whatsapp/connect", {}, { timeout: 20000 });
      const s = res.data.status || "connecting";
      setStatus(s); statusRef.current = s;
      if (res.data.error) {
        setErrorMsg(String(res.data.error));
        setLoading(false);
        // Lease-block: enrich state with diagnostic fields so UI can show countdown + force-release
        if (res.data.error === "owner_lease_held_by_another_instance") {
          _setLeaseInfo({
            blocked: true,
            owner_instance_id: res.data.lease_owner_instance_id || null,
            remaining_ms: res.data.lease_remaining_ms ?? null,
            ttl_ms: res.data.lease_ttl_ms ?? 90000,
            current_instance_id: res.data.current_instance_id || null,
          });
        }
      }
      if (res.data.qr) {
        setQrDataUrl(res.data.qr);
        setStatus("waiting_qr"); statusRef.current = "waiting_qr";
        setLoading(false);
        _setLeaseInfo(null);
      }
      if (s === "connected") {
        setConnectedOk(true);
        setLoading(false);
        _setLeaseInfo(null);
        setTimeout(() => { onConnected?.(); onClose(); }, 2000);
        return;
      }
    } catch {
      console.log("Connect request failed, will poll for QR...");
    }
    setTimeout(() => { setLoading(false); }, 8000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onConnected, onClose]);

  useEffect(() => {
    startConnection();
    const startTime = Date.now();
    pollRef.current = setInterval(async () => {
      if (statusRef.current === "connected") { clearInterval(pollRef.current); pollRef.current = null; return; }
      const elapsed = Date.now() - startTime;
      // Don't give up while lease is blocked — wait for natural expiry or force-release.
      // When not lease-blocked, stop after 30 network failures or 90s with no QR.
      const leaseBlocked = leaseInfoRef.current?.blocked;
      if (!leaseBlocked && (pollFailCount.current > 30 || elapsed > 90000)) {
        clearInterval(pollRef.current); pollRef.current = null;
        if (!qrDataUrl) {
          setStatus("connection_failed"); statusRef.current = "connection_failed";
          setErrorMsg("Nao foi possivel gerar o QR Code. Clique em 'Resetar Conexao'.");
          setLoading(false);
        }
        return;
      }
      try {
        const res = await authAxiosRef.current.get("/chatbot/whatsapp/status");
        const s = res.data.status;
        if (res.data.qr) {
          setQrDataUrl(res.data.qr);
          setStatus("waiting_qr"); statusRef.current = "waiting_qr";
          setErrorMsg(""); setStuckReconnecting(false);
          reconnectingSince.current = null;
          setLoading(false);
          pollFailCount.current = 0;
          _setLeaseInfo(null);
        } else if (res.data.error && !res.data.qr) {
          setErrorMsg(String(res.data.error));
          setLoading(false);
        } else if (s === "connected") {
          clearInterval(pollRef.current); pollRef.current = null;
          setConnectedOk(true);
          setLoading(false);
          _setLeaseInfo(null);
          toast.success("WhatsApp conectado!");
          setTimeout(() => { onConnected?.(); onClose(); }, 2000);
        } else if (s === "qr_expired") {
          setStatus("qr_expired"); statusRef.current = "qr_expired";
          setErrorMsg(res.data.error || "QR expirou. Clique em 'Resetar Sessão'.");
          setQrDataUrl(null);
          setLoading(false);
        } else if (s === "connection_failed") {
          pollFailCount.current++;
          if (pollFailCount.current > 5) {
            setStatus("connection_failed"); statusRef.current = "connection_failed";
            setErrorMsg(res.data.error || "Falha na conexao. Tente 'Resetar Sessão'.");
            setLoading(false);
          }
        } else if (s === "reconnecting" || s === "connecting") {
          if (!reconnectingSince.current) reconnectingSince.current = Date.now();
          if (Date.now() - reconnectingSince.current > 20000 && !res.data.qr && !leaseBlocked) {
            setStuckReconnecting(true);
            setLoading(false);
          }
        } else {
          pollFailCount.current = 0;
        }
      } catch { pollFailCount.current++; }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshQR = async () => {
    setLoading(true);
    setErrorMsg("");
    setQrDataUrl(null);
    pollFailCount.current = 0;
    _setLeaseInfo(null);
    try {
      const r = await authAxiosRef.current.post("/chatbot/whatsapp/refresh-qr", {}, { timeout: 20000 });
      setStatus(r.data.status || "connecting"); statusRef.current = r.data.status || "connecting";
      setQrDataUrl(r.data.qr || null);
      if (r.data.qr) setLoading(false);
    } catch {
      console.log("Refresh QR request timed out, polling...");
    }
    if (!pollRef.current) {
      statusRef.current = "connecting";
      pollRef.current = setInterval(async () => {
        try {
          const res = await authAxiosRef.current.get("/chatbot/whatsapp/status");
          if (res.data.qr) {
            setQrDataUrl(res.data.qr);
            setStatus("waiting_qr"); statusRef.current = "waiting_qr";
            setLoading(false);
            setErrorMsg("");
            _setLeaseInfo(null);
          }
          if (res.data.status === "connected") {
            clearInterval(pollRef.current); pollRef.current = null;
            setConnectedOk(true); setLoading(false);
          }
        } catch {}
      }, 2500);
    }
  };

  const resetConnection = async () => {
    setResetting(true);
    setQrDataUrl(null);
    setErrorMsg("");
    setStuckReconnecting(false);
    reconnectingSince.current = null;
    setStatus("connecting"); statusRef.current = "connecting";
    pollFailCount.current = 0;
    _setLeaseInfo(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    try {
      await authAxiosRef.current.post("/chatbot/whatsapp/reset-session", {}, { timeout: 15000 });
      toast.info("Sessão resetada. Gerando novo QR...");
    } catch {}
    await new Promise(r => setTimeout(r, 1500));
    setResetting(false);
    await startConnection();
    pollRef.current = setInterval(async () => {
      if (statusRef.current === "connected") { clearInterval(pollRef.current); pollRef.current = null; return; }
      if (pollFailCount.current > 30) {
        clearInterval(pollRef.current); pollRef.current = null;
        setStatus("connection_failed");
        setErrorMsg("Timeout. Tente novamente.");
        setLoading(false);
        return;
      }
      try {
        const res = await authAxiosRef.current.get("/chatbot/whatsapp/status");
        if (res.data.qr) {
          setQrDataUrl(res.data.qr); setStatus("waiting_qr"); statusRef.current = "waiting_qr"; setErrorMsg(""); setLoading(false);
        }
        if (res.data.status === "connected") {
          clearInterval(pollRef.current); pollRef.current = null; setConnectedOk(true); setLoading(false); toast.success("WhatsApp conectado!");
        }
        if (res.data.status === "qr_expired") {
          setStatus("qr_expired"); statusRef.current = "qr_expired"; setErrorMsg("QR expirou."); setLoading(false);
        }
      } catch { pollFailCount.current++; }
    }, 2500);
  };

  // Force-release the owner lease without wiping session credentials.
  // Use when a stale post-deploy instance holds the lease.
  const releaseLease = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    _setLeaseInfo(null);
    try {
      await authAxiosRef.current.post("/chatbot/whatsapp/release-lease", {}, { timeout: 10000 });
      toast.info("Lease liberado. Reconectando...");
    } catch (e) {
      console.warn("release-lease failed:", e);
    }
    setStatus("connecting"); statusRef.current = "connecting";
    pollFailCount.current = 0;
    await startConnection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startConnection]);

  return { qrDataUrl, status, loading, connectedOk, errorMsg, resetting, stuckReconnecting, leaseInfo, refreshQR, resetConnection, releaseLease };
}
